import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Question, Dimension, QuestionBranch } from '../../types';
import { getScoringRulesMap } from '../../utils/questionUtils';
import { questionnaireService } from '../../services/questionnaireService';
import { questionService } from '../../services/questionService';
import { questionBranchService } from '../../services/questionBranchService';
import { dimensionService } from '../../services/dimensionService';
import toast from 'react-hot-toast';
import { XMarkIcon, PlusIcon, LinkIcon, Bars3Icon } from '@heroicons/react/24/outline';

interface QuestionEditorProps {
  questionId?: string;
  questionnaireId: string;
  question?: Question; // Pass question data directly from questionnaire
  onClose: () => void;
  onSave: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  questionId,
  questionnaireId,
  question: questionProp,
  onClose,
  onSave,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Question>>({
    questionText: '',
    questionType: 'binary',
    scoringRules: undefined,
    weighting: 1,
    dimensionId: 0,
    tags: [],
    dependencies: undefined,
    isAiQuestion: false,
  });

  const [scoringRulesPairs, setScoringRulesPairs] = useState<Array<{ option: string; score: string }>>([{ option: '', score: '' }]);
  const [tagsInput, setTagsInput] = useState('');
  const [dependenciesInput, setDependenciesInput] = useState('');

  // Child question management
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showLinkChildModal, setShowLinkChildModal] = useState(false);
  const [selectedOptionForChild, setSelectedOptionForChild] = useState<string>('');
  const [selectedChildQuestionId, setSelectedChildQuestionId] = useState<string>('');
  const [childSearchQuery, setChildSearchQuery] = useState<string>('');
  const [isChildDropdownOpen, setIsChildDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Clean outside click to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsChildDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch existing branches for this question
  // Use numeric ID if available from prop, otherwise questionId
  const branchesQueryId = questionProp?.id ? String(questionProp.id) : questionId;

  const { data: existingBranches, refetch: refetchBranches } = useQuery(
    ['question-branches', branchesQueryId],
    () => questionBranchService.getBranchesForParent(branchesQueryId!),
    {
      enabled: !!branchesQueryId,
    }
  );

  // Fetch all questions in the questionnaire for linking
  const { data: questionnaireData } = useQuery(
    ['questionnaire', questionnaireId],
    () => questionnaireService.getQuestionnaire(questionnaireId),
    {
      enabled: !!questionnaireId,
    }
  );

  // Calculate weight percentage
  const calculateCurrentWeightPercentage = () => {
    if (!questionnaireData?.questions || !formData.dimensionId) return null;

    const currentWeight = typeof formData.weighting === 'string' ? parseFloat(formData.weighting) : (formData.weighting || 0);
    const currentId = questionId || 'new-question';
    const parentDbId = questionProp?.id || existingQuestion?.id;

    // Simple estimation based on dimension for now, as full group logic is complex to replicate here
    // Find other questions in this dimension
    const dimensionQuestions = questionnaireData.questions.filter(q =>
      (q.dimensionId || 0) === formData.dimensionId &&
      (parentDbId ? q.id !== parentDbId : (q.label || q.questionId) !== (questionId || ''))
    );

    // Sum of weights of other questions + current weight
    let totalWeight = currentWeight;
    dimensionQuestions.forEach(q => {
      // Use raw weighting logic
      const w = typeof q.weighting === 'string' ? parseFloat(q.weighting) : (q.weighting || 0); // fallback to 0 if null/undefined
      totalWeight += w > 0 ? w : 1; // Default to 1 if 0/null/undefined (standard behavior)
    });

    if (totalWeight === 0) return 0;
    return ((currentWeight / totalWeight) * 100).toFixed(1);
  };

  // Use question prop if provided, otherwise fetch
  const { data: existingQuestion, isLoading: loadingQuestion } = useQuery(
    ['question', questionId],
    () => questionService.getQuestion(questionId!),
    {
      enabled: !!questionId && !questionProp,
      onSuccess: (data) => {
        if (data) {
          loadQuestionData(data);
        }
      },
    }
  );

  // Load question data when question prop changes
  React.useEffect(() => {
    if (questionProp) {
      loadQuestionData(questionProp);
    } else if (existingQuestion) {
      loadQuestionData(existingQuestion);
    } else if (!questionId) {
      // New question - check for context from add question menu
      const context = (window as any).__addQuestionContext;
      if (context) {
        if (context.dimensionId) {
          setFormData(prev => ({ ...prev, dimensionId: context.dimensionId }));
        }
        // Initialize scoring rules
        setScoringRulesPairs([{ option: '', score: '' }]);
        // Clear context after using it
        delete (window as any).__addQuestionContext;
      } else {
        // Just adding a new question without context
        setScoringRulesPairs([{ option: '', score: '' }]);
      }
    }
  }, [questionProp, existingQuestion, questionId]);

  const loadQuestionData = (data: Question) => {
    const scoringMap = getScoringRulesMap(data);
    setFormData({
      questionText: data.questionText,
      questionType: data.questionType,
      scoringRules: Object.keys(scoringMap).length > 0 ? scoringMap : undefined,
      weighting: typeof data.weighting === 'string' ? parseFloat(data.weighting) : (data.weighting || 1),
      dimensionId: data.dimensionId || 0,
      tags: data.tags || [],
      dependencies: data.dependencies,
      isAiQuestion: data.isAiQuestion,
    });

    // Load question options (from QuestionOption) and scores (from scoringRuleEntries) as pairs; preserve display order
    const optionsList = (data.options && data.options.length > 0)
      ? [...data.options].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map(o => o.optionValue)
      : [];
    if (Object.keys(scoringMap).length > 0) {
      const pairs = optionsList.length > 0
        ? optionsList.map(opt => ({ option: opt, score: String(scoringMap[opt] ?? '') }))
        : Object.entries(scoringMap).map(([option, score]) => ({ option, score: String(score) }));
      setScoringRulesPairs(pairs.length > 0 ? pairs : [{ option: '', score: '' }]);
    } else if (optionsList.length > 0) {
      setScoringRulesPairs(optionsList.map((opt, index) => ({ option: opt, score: String(index + 1) })));
    } else {
      setScoringRulesPairs([{ option: '', score: '' }]);
    }

    if (data.tags) {
      // We don't need to populate tagsInput string anymore as we use formData.tags directly
      setTagsInput('');
    }

    if (data.dependencies) {
      setDependenciesInput(JSON.stringify(data.dependencies, null, 2));
    }
  };

  // Fetch dimensions
  const { data: dimensions, isLoading: loadingDimensions } = useQuery(
    'dimensions',
    () => dimensionService.getDimensions()
  );

  const saveMutation = useMutation(
    async (data: Partial<Question>) => {
      if (questionId) {
        // Update existing question
        // Use the question's unique id if available, otherwise fall back to questionId
        const questionIdToUse = questionProp?.id ? questionProp.id : questionId;
        return questionnaireService.updateQuestion(questionnaireId as unknown as number, questionIdToUse, data);
      } else {
        // Create new question - check for context from add question menu
        const context = (window as any).__addQuestionContext;
        const questionData: any = {
          questionId: `q_${Date.now()}`, // Generate unique ID
          ...data,
        };

        // Add display order from context if available
        if (context?.displayOrder !== undefined) {
          questionData.displayOrder = context.displayOrder;
        }

        // Clear context after using it
        if (context) {
          delete (window as any).__addQuestionContext;
        }

        return questionnaireService.addQuestion(questionnaireId, questionData);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questionnaire', questionnaireId]);
        queryClient.invalidateQueries(['question', questionId]);
        toast.success(questionId ? 'Question updated successfully!' : 'Question added successfully!');
        onSave();
        onClose();
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to save question');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let parsedScoringRules: any = null;

    const validPairs = scoringRulesPairs.filter(pair => pair.option.trim() && pair.score.trim());

    if (formData.questionType === 'scale' || formData.questionType === 'multiple_choice' || formData.questionType === 'multiselect' || formData.questionType === 'binary') {
      if (validPairs.length === 0) {
        toast.error('At least one question option with score is required for this question type');
        return;
      }

      parsedScoringRules = {};
      for (const pair of validPairs) {
        const scoreNum = parseFloat(pair.score);
        if (isNaN(scoreNum)) {
          toast.error(`Invalid score value for "${pair.option}". Must be a number.`);
          return;
        }
        parsedScoringRules[pair.option.trim()] = scoreNum;
      }
    } else {
      // For text questions, scoring rules are optional
      if (validPairs.length > 0) {
        parsedScoringRules = {};
        for (const pair of validPairs) {
          const scoreNum = parseFloat(pair.score);
          if (isNaN(scoreNum)) {
            toast.error(`Invalid score value for "${pair.option}". Must be a number.`);
            return;
          }
          parsedScoringRules[pair.option.trim()] = scoreNum;
        }
      }
    }

    // Parse tags - already in formData.tags, but ensure we don't need to parse from tagsInput anymore
    // as we handle it on change/enter
    const parsedTags = formData.tags || [];

    // Parse dependencies
    let parsedDependencies: any = null;
    if (dependenciesInput.trim()) {
      try {
        parsedDependencies = JSON.parse(dependenciesInput);
      } catch (err) {
        toast.error('Invalid JSON format for dependencies');
        return;
      }
    }

    // Schema-aligned: single options array with score per option (no separate scoringRules)
    const optionsPayload = validPairs.map((pair, index) => {
      const scoreNum = parseFloat(pair.score);
      return {
        optionValue: pair.option.trim(),
        displayOrder: index,
        score: Number.isNaN(scoreNum) ? 0 : scoreNum,
      };
    });

    const submitData: any = {
      questionText: formData.questionText,
      questionType: formData.questionType,
      options: optionsPayload,
      weighting: formData.weighting,
      dimensionId: formData.dimensionId,
      tags: parsedTags,
      dependencies: parsedDependencies,
      isAiQuestion: formData.isAiQuestion,
    };

    saveMutation.mutate(submitData);
  };

  const questionTypes = [
    { value: 'binary', label: 'Binary (Yes/No)' },
    { value: 'scale', label: 'Scale (Never-Always, etc.)' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'multiselect', label: 'Multi-select' },
    { value: 'text', label: 'Text Input' },
  ];

  if (loadingQuestion || loadingDimensions) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full m-4 my-8">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {questionId ? 'Edit Question' : 'Add New Question'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Child Question Indicator */}
          {(questionProp?.isChild || existingQuestion?.isChild) && (
            <div className="bg-blue-50 border-1 border-blue-200 rounded-md p-4 flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-800">Child Question</h4>
                <p className="text-sm text-blue-600 mt-1">
                  This question is a child question linked to a parent question. It will only appear when specific answers are selected in the parent question.
                </p>
              </div>
            </div>
          )}

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Text *
            </label>
            <textarea
              value={formData.questionText}
              onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              required
              placeholder="Enter the question text..."
            />
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Type *
            </label>
            <select
              value={formData.questionType}
              onChange={(e) => {
                const newType = e.target.value as Question['questionType'];
                setFormData({ ...formData, questionType: newType });

                // If it's a type that requires options and we have none, ensure we have at least one empty pair
                if (['binary', 'scale', 'multiple_choice', 'multiselect'].includes(newType)) {
                  if (scoringRulesPairs.length === 0 || (scoringRulesPairs.length === 1 && !scoringRulesPairs[0].option)) {
                    // Pre-populate common options based on type
                    if (newType === 'binary') {
                      setScoringRulesPairs([
                        { option: 'Yes', score: '1' },
                        { option: 'No', score: '0' }
                      ]);
                    } else if (newType === 'scale') {
                      setScoringRulesPairs([
                        { option: 'Never', score: '0' },
                        { option: 'Sometimes', score: '1' },
                        { option: 'Always', score: '2' }
                      ]);
                    } else {
                      setScoringRulesPairs([{ option: '', score: '' }]);
                    }
                  }
                }
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            >
              {questionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dimension */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dimension *
            </label>
            <select
              value={formData.dimensionId}
              onChange={(e) => setFormData({ ...formData, dimensionId: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            >
              <option value={0}>Select a dimension...</option>
              {dimensions?.map((dim: any) => (
                <option key={dim.id} value={dim.id}>
                  {dim.label || dim.name}
                </option>
              ))}
            </select>
          </div>

          {/* Question Options (with scores) - editable list with drag-and-drop order */}
          {(formData.questionType === 'scale' || formData.questionType === 'multiple_choice' || formData.questionType === 'multiselect' || formData.questionType === 'binary') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Options *
                <span className="text-xs text-gray-500 ml-2">(Defines options and their scores; drag to reorder display order)</span>
              </label>
              <div className="flex gap-2 mb-1">
                <span className="flex-1 text-xs font-medium text-gray-500 uppercase">Option</span>
                <span className="w-24 text-xs font-medium text-gray-500 uppercase text-right">Score</span>
                <span className="w-10" aria-hidden="true" />
              </div>
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;
                  const reordered = Array.from(scoringRulesPairs);
                  const [removed] = reordered.splice(result.source.index, 1);
                  reordered.splice(result.destination.index, 0, removed);
                  setScoringRulesPairs(reordered);
                }}
              >
                <Droppable droppableId="question-options-list">
                  {(provided) => (
                    <div className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                      {scoringRulesPairs.map((pair, index) => (
                        <Draggable key={`opt-${index}`} draggableId={`opt-${index}`} index={index}>
                          {(providedDrag) => (
                            <div
                              ref={providedDrag.innerRef}
                              {...providedDrag.draggableProps}
                              className="flex gap-2 items-center"
                            >
                              <div {...providedDrag.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 -ml-1">
                                <Bars3Icon className="h-5 w-5" />
                              </div>
                              <input
                                type="text"
                                value={pair.option}
                                onChange={(e) => {
                                  const newPairs = [...scoringRulesPairs];
                                  newPairs[index].option = e.target.value;
                                  setScoringRulesPairs(newPairs);
                                }}
                                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                                placeholder="Option (e.g., Yes, No, Always)"
                              />
                              <input
                                type="number"
                                step="0.1"
                                value={pair.score}
                                onChange={(e) => {
                                  const newPairs = [...scoringRulesPairs];
                                  newPairs[index].score = e.target.value;
                                  setScoringRulesPairs(newPairs);
                                }}
                                className="w-24 border border-gray-300 rounded-md px-3 py-2 text-right"
                                placeholder="Score"
                              />
                              {scoringRulesPairs.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newPairs = scoringRulesPairs.filter((_, i) => i !== index);
                                    setScoringRulesPairs(newPairs.length > 0 ? newPairs : [{ option: '', score: '' }]);
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  aria-label="Remove option"
                                >
                                  <XMarkIcon className="h-5 w-5" />
                                </button>
                              )}
                              {scoringRulesPairs.length <= 1 && <span className="w-10" />}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <button
                type="button"
                onClick={() => setScoringRulesPairs([...scoringRulesPairs, { option: '', score: '' }])}
                className="text-sm text-blue-600 hover:text-blue-800 mt-2"
              >
                + Add Question Option
              </button>
            </div>
          )}

          {/* Weighting */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weighting
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.weighting}
              onChange={(e) => setFormData({ ...formData, weighting: parseFloat(e.target.value) || 1 })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              min="0"
              placeholder="1.0"
            />
            {calculateCurrentWeightPercentage() !== null && (
              <div className="absolute right-3 top-3.5 text-sm text-gray-500 bg-gray-50 px-2 rounded border border-gray-200">
                {calculateCurrentWeightPercentage()}% of dimension
              </div>
            )}
          </div>

          {/* AI Question (Toggle) */}
          <div className="flex items-center">
            <button
              type="button"
              role="switch"
              aria-checked={formData.isAiQuestion || false}
              onClick={() => setFormData({ ...formData, isAiQuestion: !formData.isAiQuestion })}
              className={`${formData.isAiQuestion ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`${formData.isAiQuestion ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
            <label
              onClick={() => setFormData({ ...formData, isAiQuestion: !formData.isAiQuestion })}
              className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer"
            >
              AI-generated question
            </label>
          </div>

          {/* Tags (Chips) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md bg-white min-h-[42px] focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
              {formData.tags?.map((tag, index) => (
                <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = [...(formData.tags || [])];
                      newTags.splice(index, 1);
                      setFormData({ ...formData, tags: newTags });
                    }}
                    className="ml-1 text-blue-400 hover:text-blue-600 focus:outline-none"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="flex-1 border-none focus:ring-0 min-w-[120px] p-0 text-sm outline-none"
                placeholder={formData.tags?.length ? "" : "Type tag and press comma or enter..."}
                value={tagsInput}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.includes(',')) {
                    const newTags = val.split(',').map(t => t.trim()).filter(Boolean);
                    const currentTags = formData.tags || [];
                    const uniqueNewTags = newTags.filter(t => !currentTags.includes(t));

                    if (uniqueNewTags.length > 0) {
                      setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), ...uniqueNewTags] }));
                    }
                    setTagsInput('');
                  } else {
                    setTagsInput(val);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (tagsInput.trim()) {
                      if (!(formData.tags || []).includes(tagsInput.trim())) {
                        setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tagsInput.trim()] }));
                      }
                      setTagsInput('');
                    }
                  } else if (e.key === 'Backspace' && !tagsInput && (formData.tags || []).length > 0) {
                    const newTags = [...(formData.tags || [])];
                    newTags.pop();
                    setFormData(prev => ({ ...prev, tags: newTags }));
                  }
                }}
              />
            </div>
          </div>

          {/* Dependencies (Legacy - Hidden) */}
          {/* 
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dependencies
              <span className="text-xs text-gray-500 ml-2">(JSON format - Legacy, use Child Questions below)</span>
            </label>
            <textarea
              value={dependenciesInput}
              onChange={(e) => setDependenciesInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
              rows={3}
              placeholder='{"dependsOn": "question_id", "condition": "value"}'
            />
          </div>
          */}

          {/* Child Questions Section */}
          {(formData.questionType === 'binary' || formData.questionType === 'multiple_choice' || formData.questionType === 'multiselect' || formData.questionType === 'scale') && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Child Questions
                  <span className="text-xs text-gray-500 ml-2">(Questions that appear based on answer options)</span>
                </label>
                {questionId && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (scoringRulesPairs.filter(p => p.option.trim()).length === 0) {
                          toast.error('Please add answer options first');
                          return;
                        }
                        setShowAddChildModal(true);
                      }}
                      className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add New Child
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (scoringRulesPairs.filter(p => p.option.trim()).length === 0) {
                          toast.error('Please add answer options first');
                          return;
                        }
                        setShowLinkChildModal(true);
                      }}
                      className="text-sm px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
                    >
                      <LinkIcon className="h-4 w-4" />
                      Link Existing
                    </button>
                  </div>
                )}
                {!questionId && (
                  <p className="text-xs text-gray-500 italic">
                    Save the question first to add child questions
                  </p>
                )}
              </div>

              {/* Display existing child questions grouped by Child Question */}
              {existingBranches && existingBranches.length > 0 && (
                <div className="space-y-3 mt-3">
                  {Object.entries(
                    existingBranches.reduce((acc: Record<string, { question: any, options: string[], branches: QuestionBranch[] }>, branch) => {
                      const childId = branch.childQuestionId; // Or some unique identifier for the child
                      const childKey = String(childId);

                      if (!acc[childKey]) {
                        acc[childKey] = {
                          question: branch.childQuestion,
                          options: [],
                          branches: []
                        };
                      }

                      if (branch.parentOptionValue) {
                        acc[childKey].options.push(branch.parentOptionValue);
                      }
                      acc[childKey].branches.push(branch);
                      return acc;
                    }, {})
                  ).map(([childKey, data]) => (
                    <div key={childKey} className="bg-gray-50 rounded-md p-3 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {data.question?.questionText || `Question ${childKey}`}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {data.question?.questionType}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {data.branches.map(branch => (
                            <button
                              key={branch.id}
                              type="button"
                              title={`Remove link for option "${branch.parentOptionValue}"`}
                              onClick={async () => {
                                if (confirm(`Remove link for option "${branch.parentOptionValue}"?`)) {
                                  try {
                                    await questionBranchService.deleteBranch(branch.id);
                                    toast.success('Link removed');
                                    refetchBranches();
                                    onSave();
                                  } catch (error: any) {
                                    toast.error(error.message || 'Failed to remove link');
                                  }
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200 shadow-sm"
                            >
                              <XMarkIcon className="h-3 w-3" />
                              <span>Unlink "{branch.parentOptionValue}"</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Triggers on:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {data.options.map((option, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {option}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(!existingBranches || existingBranches.length === 0) && (
                <p className="text-sm text-gray-500 italic">No child questions configured. Add or link questions to show them based on answer options.</p>
              )}
            </div>
          )}



          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : questionId ? 'Update Question' : 'Add Question'}
            </button>
          </div>
        </form>

        {/* Add New Child Question Modal */}
        {showAddChildModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Add New Child Question</h3>
                <button
                  onClick={() => {
                    setShowAddChildModal(false);
                    setSelectedOptionForChild('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Option *
                  </label>
                  <select
                    value={selectedOptionForChild}
                    onChange={(e) => setSelectedOptionForChild(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Select an option...</option>
                    {scoringRulesPairs
                      .filter(pair => pair.option.trim())
                      .map((pair, idx) => (
                        <option key={idx} value={pair.option.trim()}>
                          {pair.option.trim()}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    The child question will appear when this option is selected
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddChildModal(false);
                      setSelectedOptionForChild('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedOptionForChild) {
                        toast.error('Please select an option');
                        return;
                      }
                      if (!questionId) {
                        toast.error('Please save the question first before adding child questions');
                        return;
                      }
                      // Open question editor in a new context to create child question
                      // For now, we'll create a placeholder and let user edit it
                      const newChildQuestionId = `q_child_${Date.now()}`;
                      try {
                        // Create the child question first
                        const childQuestion = await questionnaireService.addQuestion(questionnaireId, {
                          questionId: newChildQuestionId,
                          questionText: 'New child question (click to edit)',
                          questionType: 'text',
                          dimensionId: formData.dimensionId || 0,
                          weighting: 1,
                          tags: [],
                          isAiQuestion: false,
                        });

                        // Then create the branch
                        const parentDbId = questionProp?.id || existingQuestion?.id;
                        await questionBranchService.createBranch({
                          parentQuestionId: parentDbId ? String(parentDbId) : questionId,
                          parentOptionValue: selectedOptionForChild,
                          childQuestionId: String(childQuestion.id),
                        });

                        toast.success('Child question created! Edit it to customize.');
                        setShowAddChildModal(false);
                        setSelectedOptionForChild('');
                        refetchBranches();
                        onSave();
                      } catch (error: any) {
                        toast.error(error.message || 'Failed to create child question');
                      }
                    }}
                    className="btn btn-primary"
                  >
                    Create & Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link Existing Child Question Modal */}
        {showLinkChildModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4 max-h-[80vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Link Existing Question as Child</h3>
                <button
                  onClick={() => {
                    setShowLinkChildModal(false);
                    setSelectedOptionForChild('');
                    setSelectedChildQuestionId('');
                    setChildSearchQuery('');
                    setIsChildDropdownOpen(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Option *
                  </label>
                  <select
                    value={selectedOptionForChild}
                    onChange={(e) => setSelectedOptionForChild(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Select an option...</option>
                    {scoringRulesPairs
                      .filter(pair => pair.option.trim())
                      .map((pair, idx) => (
                        <option key={idx} value={pair.option.trim()}>
                          {pair.option.trim()}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Child Question *
                  </label>
                  {(() => {
                    const parentDbId = questionProp?.id || existingQuestion?.id;
                    const candidates = (questionnaireData?.questions || [])
                      .filter(q => {
                        const isSelf = parentDbId ? q.id === parentDbId : (q.label || q.questionId) !== questionId;
                        return !isSelf && !q.isChild;
                      });

                    const parentDimId = formData.dimensionId || questionProp?.dimensionId || 0;
                    const sameDimCandidates = candidates.filter(q => q.dimensionId === parentDimId);
                    const otherDimCandidates = candidates.filter(q => q.dimensionId !== parentDimId);

                    const filteredSameDim = sameDimCandidates.filter(q =>
                      q.questionText.toLowerCase().includes(childSearchQuery.toLowerCase()) ||
                      (q.label || '').toLowerCase().includes(childSearchQuery.toLowerCase())
                    );
                    const filteredOtherDim = otherDimCandidates.filter(q =>
                      q.questionText.toLowerCase().includes(childSearchQuery.toLowerCase()) ||
                      (q.label || '').toLowerCase().includes(childSearchQuery.toLowerCase())
                    );

                    const selectedChildQuestion = questionnaireData?.questions?.find(q => String(q.id) === selectedChildQuestionId);

                    return (
                      <div ref={dropdownRef} className="relative">
                        <div className="flex border border-gray-300 rounded-md shadow-sm overflow-hidden bg-white focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                          <input
                            type="text"
                            className="w-full px-3 py-2 text-sm focus:outline-none"
                            placeholder={selectedChildQuestion ? `${selectedChildQuestion.label || ''}: ${selectedChildQuestion.questionText}` : "Search and select a question..."}
                            value={isChildDropdownOpen ? childSearchQuery : (selectedChildQuestion ? `${selectedChildQuestion.label || ''}: ${selectedChildQuestion.questionText}` : '')}
                            onChange={(e) => {
                              setChildSearchQuery(e.target.value);
                              setIsChildDropdownOpen(true);
                            }}
                            onFocus={() => {
                              setIsChildDropdownOpen(true);
                              setChildSearchQuery('');
                            }}
                          />
                          {selectedChildQuestionId && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedChildQuestionId('');
                                setChildSearchQuery('');
                              }}
                              className="px-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsChildDropdownOpen(!isChildDropdownOpen)}
                            className="px-2 border-l border-gray-300 text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <svg className={`h-4 w-4 transform transition-transform ${isChildDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {isChildDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredSameDim.length === 0 && filteredOtherDim.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-500 italic">No matching questions found</div>
                            ) : (
                              <>
                                {filteredSameDim.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1.5 bg-blue-50 text-[10px] font-bold text-blue-800 uppercase tracking-wider sticky top-0">
                                      Same Dimension: {filteredSameDim[0]?.dimension?.name || 'Agility'}
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                      {filteredSameDim.map((q) => (
                                        <button
                                          key={q.id}
                                          type="button"
                                          onClick={() => {
                                            setSelectedChildQuestionId(String(q.id));
                                            setIsChildDropdownOpen(false);
                                          }}
                                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex flex-col gap-0.5 focus:outline-none ${selectedChildQuestionId === String(q.id) ? 'bg-blue-50/50' : ''}`}
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 font-mono">
                                              {q.label || `Q${q.id}`}
                                            </span>
                                            <span className="text-[9px] text-gray-400 font-mono uppercase bg-gray-100 px-1 py-0.5 rounded">
                                              {q.questionType}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-800 font-normal line-clamp-2">{q.questionText}</div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {filteredOtherDim.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1.5 bg-gray-100 text-[10px] font-bold text-gray-600 uppercase tracking-wider sticky top-0">
                                      Other Dimensions
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                      {filteredOtherDim.map((q) => (
                                        <button
                                          key={q.id}
                                          type="button"
                                          onClick={() => {
                                            setSelectedChildQuestionId(String(q.id));
                                            setIsChildDropdownOpen(false);
                                          }}
                                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex flex-col gap-0.5 focus:outline-none ${selectedChildQuestionId === String(q.id) ? 'bg-blue-50/50' : ''}`}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-1.5">
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-800 font-mono">
                                                {q.label || `Q${q.id}`}
                                              </span>
                                              <span className="text-[9px] text-gray-400 font-medium italic">
                                                ({q.dimension?.name || 'Other'})
                                              </span>
                                            </div>
                                            <span className="text-[9px] text-gray-400 font-mono uppercase bg-gray-100 px-1 py-0.5 rounded">
                                              {q.questionType}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-800 font-normal line-clamp-2">{q.questionText}</div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-xs text-gray-500 mt-1">
                    Select an existing question to link as a child
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowLinkChildModal(false);
                      setSelectedOptionForChild('');
                      setSelectedChildQuestionId('');
                      setChildSearchQuery('');
                      setIsChildDropdownOpen(false);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedOptionForChild || !selectedChildQuestionId) {
                        toast.error('Please select both option and question');
                        return;
                      }
                      if (!questionId) {
                        toast.error('Please save the question first before linking child questions');
                        return;
                      }
                      try {
                        const parentDbId = questionProp?.id || existingQuestion?.id;
                        await questionBranchService.createBranch({
                          parentQuestionId: parentDbId ? String(parentDbId) : questionId,
                          parentOptionValue: selectedOptionForChild,
                          childQuestionId: selectedChildQuestionId,
                        });
                        toast.success('Child question linked successfully!');
                        setShowLinkChildModal(false);
                        setSelectedOptionForChild('');
                        setSelectedChildQuestionId('');
                        setChildSearchQuery('');
                        setIsChildDropdownOpen(false);
                        refetchBranches();
                        onSave();
                      } catch (error: any) {
                        toast.error(error.message || 'Failed to link child question');
                      }
                    }}
                    className="btn btn-primary"
                  >
                    Link Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionEditor;

